/**
 * Solutions tab — the middle rung: the hand-picked solutions of a question.
 *
 * One table row per student solution; the chain column holds one chip per CELL,
 * where a cell is one prompt execution at one pipeline step (`extract_visuals`,
 * then `analyze_visual_*`). Judging the final assessment alone cannot separate
 * a misread from a misjudgement, which is the decision this screen exists to
 * support. See docs/FeatureDesigns/Evals/assessment-eval-bench.md §3.1, §7.2.
 *
 * Its real job is choosing what to open: the counts and filters exist to answer
 * "which solution do I review next", and the row drawer is where a verdict is
 * actually recorded.
 */

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    DataTable, SearchFilterBar,
    type ColumnDef, type FilterDef, type RowMenuItem, type SortState,
} from '@shared-ui/components';
import { adminApi } from '../../api/adminApi';
import type { MatrixRow, MatrixCell, VariantQuality, StepRole } from '../../types/scenarioEvals';
import { BenchRowView } from './BenchRowView';
import { DISPUTE_MARGIN } from './benchQuestionRollup';
import './BenchTab.css';

export function BenchTab({ datasetId, questionFilter, onClearQuestionFilter }: {
    datasetId: string;
    /** Set when arriving from the Questions tab. It seeds the question filter
     *  rather than filtering separately — two mechanisms narrowing the same
     *  list is one more than can stay consistent. */
    questionFilter?: string;
    onClearQuestionFilter?: () => void;
}) {
    const qc = useQueryClient();
    const [openRow, setOpenRow] = useState<{ rowId: string; outputId?: string } | null>(null);
    const [showHistorical, setShowHistorical] = useState(true);
    const [filters, setFilters] = useState<Record<string, string>>(
        questionFilter ? { question: questionFilter } : {},
    );
    const [sort, setSort] = useState<SortState>({ sortBy: 'spread', sortOrder: 'desc' });

    const { data: matrix, isLoading, error } = useQuery({
        queryKey: ['benchMatrix', datasetId, showHistorical],
        queryFn: () => adminApi.getBenchMatrix(datasetId, showHistorical),
        enabled: !!datasetId,
    });

    useEffect(() => {
        if (questionFilter) setFilters(f => ({ ...f, question: questionFilter }));
    }, [questionFilter]);

    /** Once the reader touches the bar, the arrival filter has done its job —
     *  drop it upstream so switching tabs doesn't resurrect it. */
    const applyFilters = (next: Record<string, string>) => {
        setFilters(next);
        if (questionFilter && next.question !== questionFilter) onClearQuestionFilter?.();
    };

    const refresh = () => qc.invalidateQueries({ queryKey: ['benchMatrix', datasetId] });

    /** Removing a row drops it from the bench only. The submission, its
     *  ModelInput and every output it produced are untouched — this is
     *  un-picking a choice, not deleting evidence. */
    const removeRow = useMutation({
        mutationFn: (rowId: string) => adminApi.deleteScenarioInput(datasetId, rowId),
        onSuccess: refresh,
    });

    const bundle = useMutation({
        mutationFn: () => adminApi.getBenchBundle(datasetId),
        onSuccess: (res) => {
            // Saved rather than shown: the point of the bundle is to be read
            // offline in one pass, not scrolled in a browser.
            const blob = new Blob([res.jsonl], { type: 'application/x-ndjson' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `bench-${datasetId}.jsonl`;
            a.click();
            URL.revokeObjectURL(a.href);
        },
    });

    if (isLoading) return <p className="bench-muted">Loading solutions…</p>;
    if (error) return <p className="bench-error">{String(error)}</p>;
    if (!matrix) return null;

    const openedRow = openRow ? matrix.rows.find(r => r.input.id === openRow.rowId) : undefined;
    const rows = matrix.rows;
    const visible = sortRows(rows.filter(r => matchesFilters(r, filters)), sort);
    const filterDefs = buildFilters(questionOptions(matrix.rows));

    const columns: ColumnDef<MatrixRow>[] = [
        {
            key: 'solution',
            header: 'Solution',
            mobile: 'title',
            render: row => <SolutionCell row={row} index={rows.indexOf(row) + 1} />,
        },
        {
            key: 'student',
            header: 'Student',
            sortable: true,
            render: row => (row.userName || row.userId
                ? <span title={row.userId}>{row.userName ?? row.userId!.slice(-6)}</span>
                : <span className="bench-muted">—</span>),
        },
        {
            key: 'submitted',
            header: 'Submitted',
            sortable: true,
            className: 'bench-num',
            render: row => (row.submittedAt
                ? <span title={new Date(row.submittedAt).toLocaleString()}>
                    {row.submittedAt.slice(0, 10)}
                </span>
                : <span className="bench-muted">—</span>),
        },
        {
            key: 'spread',
            header: 'Scores',
            sortable: true,
            className: 'bench-num',
            render: row => {
                const spread = scoreSpread(row);
                if (!spread) return <span className="bench-muted">—</span>;
                return (
                    <span className={spread.disputed ? 'bench-disputed' : ''}
                        title={spread.disputed
                            ? 'Two models scored this same solution more than 3 points apart — read this one first.'
                            : 'What the assessment steps scored this solution.'}>
                        {spread.text}
                    </span>
                );
            },
        },
        {
            key: 'human',
            header: 'Your verdicts',
            sortable: true,
            render: row => <Coverage judged={countVerdicts(row, 'human')} of={allCells(row).length} />,
        },
        {
            key: 'judge',
            header: 'Judge',
            sortable: true,
            render: row => <Coverage judged={countVerdicts(row, 'judge')} of={allCells(row).length} />,
        },
        {
            key: 'chain',
            header: 'Chain',
            mobile: 'hide',
            render: row => <ChainCells row={row} showHistorical={showHistorical}
                onOpen={outputId => setOpenRow({ rowId: row.input.id, outputId })} />,
        },
    ];

    return (
        <div className="bench">
            <div className="bench-toolbar">
                <div className="bench-counts">
                    <Stat label="rows" value={matrix.counts.rows} />
                    <Stat label="cells" value={matrix.counts.cells} />
                    <Stat label="judged" value={matrix.counts.judged} />
                    {matrix.counts.stale > 0 && (
                        <Stat label="stale" value={matrix.counts.stale} warn
                            title="Verdicts made against an older version of the row — the solution has been edited since." />
                    )}
                </div>
                <div className="bench-actions">
                    <label className="bench-check">
                        <input type="checkbox" checked={showHistorical} onChange={e => setShowHistorical(e.target.checked)} />
                        Show historical cells
                    </label>
                    <button className="btn btn-secondary" disabled={bundle.isPending} onClick={() => bundle.mutate()}>
                        {bundle.isPending ? 'Exporting…' : 'Export bundle (.jsonl)'}
                    </button>
                </div>
            </div>

            <ChainLegend />

            <DataTable
                columns={columns}
                data={visible}
                rowKey={row => row.input.id}
                sort={sort}
                onSortChange={setSort}
                onRowClick={row => setOpenRow({ rowId: row.input.id })}
                rowMenu={(row): RowMenuItem[] => [
                    {
                        key: 'remove',
                        label: 'Remove from bench',
                        danger: true,
                        onSelect: () => {
                            const judged = countVerdicts(row, 'human') + countVerdicts(row, 'judge');
                            const warning = judged
                                ? `This solution has ${judged} verdict${judged === 1 ? '' : 's'}, which will be orphaned. `
                                : '';
                            if (window.confirm(`${warning}Remove this solution from the bench? The submission itself is not deleted.`)) {
                                removeRow.mutate(row.input.id);
                            }
                        },
                    },
                ]}
                mobileCards
                toolbar={
                    <SearchFilterBar
                        filters={filterDefs}
                        values={filters}
                        onChange={applyFilters}
                        searchPlaceholder="question, student, tag"
                        // Everything filters in memory over one fetched matrix,
                        // so there is nothing for a Fetch button to commit.
                        hideFetch
                    />
                }
                emptyMessage={matrix.rows.length
                    ? 'No solutions match the current filters.'
                    : 'No rows yet. Add a question on the Questions tab, or use “Add to bench” on a Solution Analysis in Operations.'}
            />

            {openedRow && (
                <BenchRowView
                    datasetId={datasetId}
                    row={openedRow}
                    focusOutputId={openRow?.outputId}
                    onClose={() => setOpenRow(null)}
                    onChanged={refresh}
                />
            )}
        </div>
    );
}

function Stat({ label, value, warn, title }: { label: string; value: number; warn?: boolean; title?: string }) {
    return (
        <span className={`bench-stat${warn ? ' warn' : ''}`} title={title}>
            <strong>{value}</strong> {label}
        </span>
    );
}

// ─── Row cells ─────────────────────────────────────────────────────────

/**
 * A bench is normally many solutions to the SAME question, so the question text
 * is the one thing that cannot tell rows apart. The work itself can.
 */
function SolutionCell({ row, index }: { row: MatrixRow; index: number }) {
    const sol = row.input.studentSolution;

    return (
        <div className="bench-solcell">
            {sol?.imageUrl
                ? <img className="bench-row-thumb" src={sol.imageUrl} alt={`Solution ${index}`} loading="lazy" />
                : <div className="bench-row-thumb typed">{(sol?.textAnswer || '—').slice(0, 60)}</div>}

            <div className="bench-row-body">
                <div className="bench-row-title">
                    <span className="bench-row-n">#{index}</span>
                    <span className={`bench-variant ${row.input.variantQuality ?? 'unset'}`}>
                        {variantLabel(row.input.variantQuality)}
                    </span>
                </div>
                <div className="bench-row-meta">
                    {(row.input.difficultyTags ?? []).map(t => <span key={t} className="bench-tag">{t}</span>)}
                    {!row.input.label && <span className="bench-pill unlabeled" title="No ground truth yet — verdicts still work">unlabeled</span>}
                </div>
            </div>
        </div>
    );
}

/** The chain as it ran, one chip per prompt execution. Clicking a chip opens
 *  the row at that step rather than at the top — the reason to keep the chips
 *  in a table that already carries the counts. */
function ChainCells({ row, showHistorical, onOpen }: {
    row: MatrixRow; showHistorical: boolean; onOpen: (outputId: string) => void;
}) {
    if (!row.chains.length) {
        return (
            <span className="bench-muted">
                {showHistorical ? 'no runs' : 'no bench runs — tick “Show historical cells”'}
            </span>
        );
    }
    return (
        <div className="bench-chains-cell">
            {row.chains.map((chain, i) => (
                <div className="bench-chain" key={`${chain.runId ?? 'hist'}-${chain.pipelineName}-${i}`}>
                    {row.chains.length > 1 && (
                        <span className="bench-chain-label" title={chain.pipelineName}>
                            {chain.pipelineConfigName ?? chain.pipelineName}
                        </span>
                    )}
                    <div className="bench-chain-cells">
                        {chain.cells.map((cell, j) => (
                            <span key={cell.outputId} className="bench-cell-wrap">
                                <CellChip cell={cell} onClick={() => onOpen(cell.outputId)} />
                                {j < chain.cells.length - 1 && <span className="bench-arrow">→</span>}
                            </span>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

/**
 * What the chain chips mean.
 *
 * Only the things a chip says in colour or shape. The step is written on the
 * chip in words, so it needs no key — which is the point: the legend is short
 * because the encoding is.
 */
function ChainLegend() {
    return (
        <div className="bench-legend">
            <span className="bench-legend-title">Chain</span>
            <span className="bench-legend-item"><i className="swatch fill v-good" /> your verdict fills the chip</span>
            <span className="bench-legend-item"><i className="bench-judge-dot j-good legend-dot" /> the judge's is the dot</span>
            <span className="bench-legend-item"><i className="swatch fill historical" /> dashed = shipped in production, not replayed on the bench</span>
            <span className="bench-legend-item"><b>9</b> = the score that step gave</span>
        </div>
    );
}

/** Coverage, not a percentage: "0 of 3" is the honest reading when nothing has
 *  been judged, where 0% looks like a score. */
function Coverage({ judged, of }: { judged: number; of: number }) {
    if (!of) return <span className="bench-muted">—</span>;
    return (
        <span className={judged === of ? 'bench-covered' : 'bench-muted'}>
            {judged} of {of}
        </span>
    );
}

function CellChip({ cell, onClick }: { cell: MatrixCell; onClick: () => void }) {
    const human = cell.humanVerdict?.verdict;
    const judge = cell.judgeVerdict?.verdict;
    const cls = [
        'bench-cell',
        `role-${cell.role}`,
        human ? `v-${human}` : 'v-none',
        cell.historical ? 'historical' : '',
        cell.staleVerdict ? 'stale' : '',
        cell.status === 'skipped' ? 'skipped' : '',
    ].filter(Boolean).join(' ');

    const title = [
        `${cell.stepType} (${cell.role})`,
        cell.provider ? `provider: ${cell.provider}` : '',
        cell.promptId ? `prompt: ${cell.promptId}@v${cell.promptVersion}` : '',
        cell.score !== undefined ? `scored this solution: ${cell.score}/10` : '',
        human ? `you: ${human}` : 'unjudged',
        judge ? `judge: ${judge}` : '',
        cell.historical ? 'shipped in production — scored apart from replayed cells, not dropped' : '',
        cell.staleVerdict ? 'verdict is stale (row edited since)' : '',
    ].filter(Boolean).join('\n');

    return (
        <button type="button" className={cls} title={title} onClick={onClick}>
            <span className="bench-cell-step">{stepLabel(cell.role)}</span>
            <span className="bench-cell-model">{cell.provider ?? '—'}</span>
            {cell.score !== undefined && <span className="bench-cell-score">{cell.score}</span>}
            {judge && <span className={`bench-judge-dot j-${judge}`} title={`judge: ${judge}`} />}
        </button>
    );
}

/**
 * The scores this row's assessment cells landed on.
 *
 * A wide spread is the signal to read a row: two models looking at the same
 * work and landing 0 and 10 apart is precisely the failure the bench exists to
 * find, and it is invisible when every row header shows the same question.
 */
function scoreSpread(row: MatrixRow): { text: string; disputed: boolean } | null {
    const scores = assessmentScores(row);
    if (!scores.length) return null;

    const min = Math.min(...scores), max = Math.max(...scores);
    return {
        text: min === max ? `${min}` : `${min}–${max}`,
        disputed: max - min > DISPUTE_MARGIN,
    };
}

// ─── filtering + sorting ───────────────────────────────────────────────

/**
 * In-memory over one fetched matrix — there is no server-side query to commit,
 * so the bar auto-applies. The filters are the questions actually asked of this
 * screen: what is left to judge, what the models fought over, what kind of
 * answer it was.
 */
function buildFilters(questions: Array<{ id: string; label: string }>): FilterDef[] {
  return [
    {
        key: 'question',
        placeholder: 'Any question',
        // Options come from the rows on hand, so the picker only ever offers
        // questions that have some. It is rendered even when there is one of
        // them: a control that comes and goes with the data is harder to trust
        // than one that is always in the same place.
        options: questions.map(q => ({ value: q.id, label: q.label })),
    },
    {
        key: 'judged',
        placeholder: 'Any verdict state',
        options: [
            { value: 'unjudged', label: 'Not judged by you' },
            { value: 'partial', label: 'Partly judged' },
            { value: 'judged', label: 'Fully judged' },
        ],
    },
    {
        key: 'disputed',
        placeholder: 'Any agreement',
        options: [
            { value: 'disputed', label: 'Models disagree' },
            { value: 'comparable', label: 'Two or more models ran' },
            { value: 'single', label: 'Only one model ran' },
        ],
    },
    {
        key: 'variant',
        placeholder: 'Any variant',
        options: [
            { value: 'unset', label: 'Unset' },
            { value: 'correct', label: 'Correct' },
            { value: 'partially_correct', label: 'Partially correct' },
            { value: 'common_mistake', label: 'Common mistake' },
        ],
    },
    {
        key: 'label',
        placeholder: 'Any label state',
        options: [
            { value: 'unlabeled', label: 'Unlabeled' },
            { value: 'labeled', label: 'Labeled' },
        ],
    },
  ];
}

export function matchesFilters(row: MatrixRow, filters: Record<string, string>): boolean {
    if (filters.question && row.input.question?.id !== filters.question) return false;

    const cells = allCells(row);
    const judged = countVerdicts(row, 'human');

    if (filters.judged === 'unjudged' && judged > 0) return false;
    if (filters.judged === 'partial' && (judged === 0 || judged === cells.length)) return false;
    if (filters.judged === 'judged' && (!cells.length || judged < cells.length)) return false;

    if (filters.disputed) {
        const scores = assessmentScores(row);
        const comparable = scores.length > 1;
        if (filters.disputed === 'disputed' && !(comparable && Math.max(...scores) - Math.min(...scores) > DISPUTE_MARGIN)) return false;
        if (filters.disputed === 'comparable' && !comparable) return false;
        if (filters.disputed === 'single' && comparable) return false;
    }

    if (filters.variant && (row.input.variantQuality ?? 'unset') !== filters.variant) return false;
    if (filters.label === 'unlabeled' && row.input.label) return false;
    if (filters.label === 'labeled' && !row.input.label) return false;

    const q = filters.search?.trim().toLowerCase();
    if (q) {
        const haystack = [
            row.input.question?.text,
            row.userId,
            row.input.curationNote,
            ...(row.input.difficultyTags ?? []),
            ...modelsOf(row),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
    }
    return true;
}

/** Sorting is the caller's job — DataTable renders the header state and emits
 *  the change. Scores sort on the SIZE of the disagreement, not the score:
 *  a solution both models called 10 is not more interesting than one they
 *  called 2. */
export function sortRows(rows: MatrixRow[], sort: SortState): MatrixRow[] {
    const dir = sort.sortOrder === 'asc' ? 1 : -1;
    if (sort.sortBy === 'student' || sort.sortBy === 'submitted') {
        const text = (row: MatrixRow) => (sort.sortBy === 'student'
            ? row.userName ?? row.userId ?? ''
            : row.submittedAt ?? '');
        return [...rows].sort((a, b) => text(a).localeCompare(text(b)) * dir);
    }

    const value = (row: MatrixRow): number => {
        switch (sort.sortBy) {
            case 'human': return countVerdicts(row, 'human');
            case 'judge': return countVerdicts(row, 'judge');
            default: {
                const scores = assessmentScores(row);
                return scores.length > 1 ? Math.max(...scores) - Math.min(...scores) : -1;
            }
        }
    };
    return [...rows].sort((a, b) => (value(a) - value(b)) * dir);
}

const allCells = (row: MatrixRow): MatrixCell[] => row.chains.flatMap(c => c.cells);

/** The questions these rows are of, each with a count so the picker says how
 *  much is behind an option before it is chosen. */
function questionOptions(rows: MatrixRow[]): Array<{ id: string; label: string }> {
    const byId = new Map<string, { id: string; text: string; n: number }>();
    for (const row of rows) {
        const q = row.input.question;
        if (!q?.id) continue;
        const acc = byId.get(q.id) ?? { id: q.id, text: q.text ?? q.id, n: 0 };
        acc.n++;
        byId.set(q.id, acc);
    }
    return [...byId.values()]
        .sort((a, b) => b.n - a.n)
        .map(q => ({ id: q.id, label: `${q.text.slice(0, 60)}${q.text.length > 60 ? '…' : ''} (${q.n})` }));
}

const modelsOf = (row: MatrixRow): string[] =>
    [...new Set(allCells(row).map(c => c.provider).filter((p): p is string => !!p))];

/** Assessment and combined together: a single-step pipeline that reads and
 *  judges in one call is still an assessment of this solution. */
const assessmentCells = (row: MatrixRow): MatrixCell[] =>
    allCells(row).filter(c => c.role === 'assessment' || c.role === 'combined');

const countVerdicts = (row: MatrixRow, evaluator: 'human' | 'judge'): number =>
    allCells(row).filter(c => (evaluator === 'human' ? c.humanVerdict : c.judgeVerdict)).length;

const assessmentScores = (row: MatrixRow): number[] =>
    assessmentCells(row).map(c => c.score).filter((s): s is number => s !== undefined);

// ─── helpers ───────────────────────────────────────────────────────────

function variantLabel(v?: VariantQuality): string {
    switch (v) {
        case 'correct': return 'correct';
        case 'partially_correct': return 'right/wrong-method';
        case 'common_mistake': return 'mistake';
        default: return 'unset';
    }
}

/**
 * What the step does, in a word.
 *
 * Written out rather than encoded as a colour: a hue only carries meaning
 * people already hold, which red-and-green does and purple-vs-amber does not.
 * Colour on a chip is spent on the verdict for exactly that reason.
 */
function stepLabel(role: StepRole): string {
    switch (role) {
        case 'reading': return 'read';
        case 'scoring': return 'score';
        case 'combined': return 'read+assess';
        default: return 'assess';
    }
}

export default BenchTab;
